import frontendLogger from "../utils/frontendLogger.js";
import useAuthUser from "../hooks/useAuthUser.js";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./css/App.css";
import "./css/MainPage.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { API_BASE_URL } from "../app_config.js";

const getAuthToken = () => localStorage.getItem("token");

export default function DeleteDistilleries() {
  const { username, role, loading: authLoading } = useAuthUser();
  const navigate = useNavigate();
  const [distilleries, setDistilleries] = useState([]);
  const [selectedDistilleryId, setSelectedDistilleryId] = useState("");
  const [selectedDistilleryName, setSelectedDistilleryName] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Redirect wenn nicht Admin
  useEffect(() => {
    if (!authLoading && role !== "admin") {
      navigate("/main");
    }
  }, [role, navigate, authLoading]);

  // Destillerien laden
  useEffect(() => {
    const fetchDistilleries = async () => {
      try {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/distilleries`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Fehler beim Laden der Destillerien");

        const data = await response.json();
        frontendLogger.info("Fetched distilleries:", data);

        // Filtere gelöschte Destillerien aus
        const activeDistilleries = data.filter(
          (d) => !d.name?.startsWith("deleted_distillery"),
        );
        setDistilleries(activeDistilleries);
      } catch (error) {
        frontendLogger.error("Error fetching distilleries:", error);
        setMessage("Fehler beim Laden der Destillerien");
      }
    };

    fetchDistilleries();
  }, []);

  const handleDistillerySelect = (e) => {
    const id = e.target.value;
    setSelectedDistilleryId(id);

    if (id) {
      const selected = distilleries.find((d) => d.id === parseInt(id));
      setSelectedDistilleryName(selected?.name || "");
    } else {
      setSelectedDistilleryName("");
    }
  };

  const handleDeleteClick = () => {
    if (!selectedDistilleryId) {
      setMessage("Bitte wähle eine Destillerie aus");
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    setShowConfirmation(false);

    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/api/distilleryDataCreate/${selectedDistilleryId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem("token");
          navigate("/login");
        }
        throw new Error("Fehler beim Löschen der Destillerie");
      }

      const data = await response.json();
      frontendLogger.info("Distillery deleted:", data);

      setMessage(
        data.marked
          ? `✓ "${selectedDistilleryName}" wurde als gelöscht markiert (erscheint noch in Whiskys als "deleted_distillery")`
          : `✓ "${selectedDistilleryName}" wurde vollständig gelöscht`,
      );

      // Aktualisiere die Liste
      setDistilleries(
        distilleries.filter((d) => d.id !== parseInt(selectedDistilleryId)),
      );
      setSelectedDistilleryId("");
      setSelectedDistilleryName("");
    } catch (error) {
      frontendLogger.error("Error deleting distillery:", error);
      setMessage("Fehler beim Löschen der Destillerie");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  const handleBack = () => {
    navigate("/main");
  };

  return (
    <div className="page-layout">
      <div className="menu-container">
        <div className="title-box">
          <p>Willkommen bei DRAM</p>
        </div>
        <div className="menu-box">
          <h1 data-testid="user-heading">{username}</h1>
        </div>

        <nav className="nav">
          <button
            className="nav-button"
            onClick={handleBack}
            data-testid="nav-back-button"
          >
            Zurück
          </button>
        </nav>

        <img
          src={require("../assets/logo.png")}
          alt="Versionsinformationen anzeigen"
          className="whisky-image"
          role="button"
          tabIndex={0}
        />
      </div>

      <div className="content-container">
        <div className="search-form-group">
          <form
            id="deleteForm"
            onSubmit={(e) => {
              e.preventDefault();
              handleDeleteClick();
            }}
          >
            <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
              Destillerie löschen
            </h2>

            <label htmlFor="distillery-select">
              Wähle eine Destillerie zum Löschen:
            </label>
            <select
              id="distillery-select"
              value={selectedDistilleryId}
              onChange={handleDistillerySelect}
              style={{
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #c69c6d",
                fontSize: "14px",
              }}
            >
              <option value="">-- Destillerie auswählen --</option>
              {distilleries.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({d.country})
                </option>
              ))}
            </select>

            {message && (
              <div
                style={{
                  padding: "12px",
                  backgroundColor:
                    message.startsWith("✓") || message.startsWith("Destillerie")
                      ? "#e8f5e9"
                      : "#ffebee",
                  color:
                    message.startsWith("✓") || message.startsWith("Destillerie")
                      ? "#2e7d32"
                      : "#c62828",
                  borderRadius: "5px",
                  marginTop: "10px",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              >
                {message}
              </div>
            )}

            <button
              type="submit"
              style={{
                padding: "12px 20px",
                background: "linear-gradient(135deg, #8b5e3c 0%, #5c3a21 100%)",
                border: "1px solid #c69c6d",
                borderRadius: "5px",
                color: "#f5e6d3",
                fontWeight: "600",
                cursor: selectedDistilleryId ? "pointer" : "not-allowed",
                fontSize: "14px",
                opacity: selectedDistilleryId ? 1 : 0.5,
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
              }}
              disabled={!selectedDistilleryId || loading}
              onMouseEnter={(e) => {
                if (selectedDistilleryId) {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow =
                    "0 6px 15px rgba(60, 40, 20, 0.25)";
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "none";
              }}
            >
              {loading ? "Wird gelöscht..." : "Löschen"}
            </button>
          </form>
        </div>
      </div>

      {showConfirmation && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h2>Destillerie wirklich löschen?</h2>
            <p style={{ marginBottom: "20px" }}>
              Bist du sicher, dass du die Destillerie{" "}
              <strong>"{selectedDistilleryName}"</strong> löschen möchtest?
            </p>
            <p
              style={{ fontSize: "0.9em", color: "#666", marginBottom: "20px" }}
            >
              {selectedDistilleryName?.startsWith("deleted_distillery")
                ? "Hinweis: Diese Destillerie wird noch in Whiskys angezeigt."
                : "Falls diese Destillerie in Whiskys verwendet wird, wird sie dort als 'deleted_distillery' angezeigt."}
            </p>

            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                style={{
                  padding: "10px 20px",
                  background: "#ccc",
                  border: "1px solid #999",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  transition: "background 0.2s ease",
                }}
                onMouseEnter={(e) => (e.target.style.background = "#bbb")}
                onMouseLeave={(e) => (e.target.style.background = "#ccc")}
                onClick={handleCancel}
              >
                Nein, abbrechen
              </button>
              <button
                style={{
                  padding: "10px 20px",
                  background:
                    "linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)",
                  color: "#fff",
                  border: "1px solid #b71c1c",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow = "0 6px 15px rgba(0, 0, 0, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }}
                onClick={handleConfirmDelete}
                disabled={loading}
              >
                Ja, löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
